/**
 * NeighborhoodMap — Weidner Classic
 *
 * Tower 12 neighborhood page (https://www.tower12.com/neighborhood):
 * centered subtitle, then an interactive panoramic hotspot viewer
 * with view tabs (Front / South / East) and pulsing pins that open
 * a photo + name + address tip.
 *
 * Replaces the rise Google Maps + POI list. Same slot name so the
 * inherited neighborhood page section still resolves.
 *
 * Maintenance checklist when re-syncing with the rise parent:
 *   1. Diff this file against the rise NeighborhoodMap in the sibling checkout.
 *   2. Do not reintroduce the two-column Google Maps + card list layout.
 *   3. Keep the intro subtitle above the hotspot viewer.
 *   4. Keep three view tabs with Tower 12 panoramas/POIs as defaults
 *      (Front/South up to 3 pins; East up to 7).
 *   5. Keep pin positions as % top/left fields; hide empty pins live.
 *   6. Run `pnpm run lint:schema` and `pnpm run typecheck`.
 */

import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useEditMode,
  useSlotGroup,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import imgFrontView from '../assets/neighborhood/front-view.jpg';
import imgSouthView from '../assets/neighborhood/south-view.jpg';
import imgEastView from '../assets/neighborhood/east-view.jpg';
import imgMoore from '../assets/neighborhood/moore-theatre.jpg';
import imgOlympic from '../assets/neighborhood/olympic-sculpture-park.jpg';
import imgMopop from '../assets/neighborhood/mopop.jpg';
import imgSeattleCenter from '../assets/neighborhood/seattle-center.jpg';
import imgRainier from '../assets/neighborhood/mount-rainier.jpg';
import imgBainbridge from '../assets/neighborhood/bainbridge.jpg';
import imgPike from '../assets/neighborhood/pike-place.jpg';
import imgTMobile from '../assets/neighborhood/t-mobile-park.jpg';
import imgLumen from '../assets/neighborhood/lumen-field.jpg';
import imgWheel from '../assets/neighborhood/great-wheel.jpg';

// ============================================================================
// Slot defs — views (Tower 12 panoramas as runtime fallbacks)
// ============================================================================

const view1ImageDef: SlotGroupDefinition = {
  label: 'Front View Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Front view of the neighborhood' },
  },
};

const view2ImageDef: SlotGroupDefinition = {
  label: 'South View Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'South view of the neighborhood' },
  },
};

const view3ImageDef: SlotGroupDefinition = {
  label: 'East View Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'East view of the neighborhood' },
  },
};

// ============================================================================
// Slot defs — pins (Tower 12 hotspot copy + positions)
// ============================================================================

const view1Pin1Def: SlotGroupDefinition = {
  label: 'Front View Pin 1',
  fields: {
    top: { label: 'Top %', type: 'text', defaultValue: '50' },
    left: { label: 'Left %', type: 'text', defaultValue: '93' },
    name: { label: 'Name', type: 'text', defaultValue: 'Moore Theatre' },
    address: { label: 'Address', type: 'text', defaultValue: '1932 2nd Ave, Seattle, WA 98121' },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const view1Pin2Def: SlotGroupDefinition = {
  label: 'Front View Pin 2',
  fields: {
    top: { label: 'Top %', type: 'text', defaultValue: '54' },
    left: { label: 'Left %', type: 'text', defaultValue: '39' },
    name: { label: 'Name', type: 'text', defaultValue: 'Olympic Sculpture Park' },
    address: { label: 'Address', type: 'text', defaultValue: '2901 Western Ave, Seattle, WA 98121' },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const view1Pin3Def: SlotGroupDefinition = {
  label: 'Front View Pin 3',
  fields: {
    top: { label: 'Top %', type: 'text', defaultValue: '66' },
    left: { label: 'Left %', type: 'text', defaultValue: '49' },
    name: { label: 'Name', type: 'text', defaultValue: '' },
    address: { label: 'Address', type: 'text', defaultValue: '' },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const view2Pin1Def: SlotGroupDefinition = {
  label: 'South View Pin 1',
  fields: {
    top: { label: 'Top %', type: 'text', defaultValue: '66' },
    left: { label: 'Left %', type: 'text', defaultValue: '49' },
    name: { label: 'Name', type: 'text', defaultValue: 'Museum of Pop Culture' },
    address: { label: 'Address', type: 'text', defaultValue: '325 5th Ave N, Seattle, WA 98109' },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const view2Pin2Def: SlotGroupDefinition = {
  label: 'South View Pin 2',
  fields: {
    top: { label: 'Top %', type: 'text', defaultValue: '56' },
    left: { label: 'Left %', type: 'text', defaultValue: '53' },
    name: { label: 'Name', type: 'text', defaultValue: 'Seattle Center' },
    address: { label: 'Address', type: 'text', defaultValue: '305 Harrison St, Seattle, WA 98109' },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const view2Pin3Def: SlotGroupDefinition = {
  label: 'South View Pin 3',
  fields: {
    top: { label: 'Top %', type: 'text', defaultValue: '55' },
    left: { label: 'Left %', type: 'text', defaultValue: '81' },
    name: { label: 'Name', type: 'text', defaultValue: '' },
    address: { label: 'Address', type: 'text', defaultValue: '' },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const view3Pin1Def: SlotGroupDefinition = {
  label: 'East View Pin 1',
  fields: {
    top: { label: 'Top %', type: 'text', defaultValue: '50' },
    left: { label: 'Left %', type: 'text', defaultValue: '20' },
    name: { label: 'Name', type: 'text', defaultValue: 'Mount Rainier' },
    address: { label: 'Address', type: 'text', defaultValue: 'Mount Rainier, Seattle, WA 98304' },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const view3Pin2Def: SlotGroupDefinition = {
  label: 'East View Pin 2',
  fields: {
    top: { label: 'Top %', type: 'text', defaultValue: '55' },
    left: { label: 'Left %', type: 'text', defaultValue: '81' },
    name: { label: 'Name', type: 'text', defaultValue: 'Bainbridge Island' },
    address: { label: 'Address', type: 'text', defaultValue: 'Seattle Ferry Terminal, Seattle, WA 98104' },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const view3Pin3Def: SlotGroupDefinition = {
  label: 'East View Pin 3',
  fields: {
    top: { label: 'Top %', type: 'text', defaultValue: '93' },
    left: { label: 'Left %', type: 'text', defaultValue: '24' },
    name: { label: 'Name', type: 'text', defaultValue: 'Pike Place Market' },
    address: { label: 'Address', type: 'text', defaultValue: '85 Pike St, Seattle, WA 98101' },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const view3Pin4Def: SlotGroupDefinition = {
  label: 'East View Pin 4',
  fields: {
    top: { label: 'Top %', type: 'text', defaultValue: '56' },
    left: { label: 'Left %', type: 'text', defaultValue: '28' },
    name: { label: 'Name', type: 'text', defaultValue: 'T-Mobile Park' },
    address: { label: 'Address', type: 'text', defaultValue: '1250 1st Ave S, Seattle, WA 98134' },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const view3Pin5Def: SlotGroupDefinition = {
  label: 'East View Pin 5',
  fields: {
    top: { label: 'Top %', type: 'text', defaultValue: '61' },
    left: { label: 'Left %', type: 'text', defaultValue: '23' },
    name: { label: 'Name', type: 'text', defaultValue: 'Lumen Field' },
    address: { label: 'Address', type: 'text', defaultValue: '800 Occidental Ave S, Seattle, WA 98134' },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const view3Pin6Def: SlotGroupDefinition = {
  label: 'East View Pin 6',
  fields: {
    top: { label: 'Top %', type: 'text', defaultValue: '71' },
    left: { label: 'Left %', type: 'text', defaultValue: '34' },
    name: { label: 'Name', type: 'text', defaultValue: 'The Seattle Great Wheel' },
    address: { label: 'Address', type: 'text', defaultValue: '1301 Alaskan Way, Seattle, WA 98101' },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const view3Pin7Def: SlotGroupDefinition = {
  label: 'East View Pin 7',
  fields: {
    top: { label: 'Top %', type: 'text', defaultValue: '86' },
    left: { label: 'Left %', type: 'text', defaultValue: '36' },
    name: { label: 'Name', type: 'text', defaultValue: 'Bainbridge Island Ferry' },
    address: { label: 'Address', type: 'text', defaultValue: 'Seattle Ferry Terminal, Seattle, WA 98104' },
    image: { label: 'Image', type: 'image', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Helpers
// ============================================================================

type PinData = {
  top?: string | null;
  left?: string | null;
  name?: string | null;
  address?: string | null;
  image?: string | null;
  url?: string | null;
  _target?: string | null;
};

function parsePercent(raw: string | null | undefined, fallback: number): number {
  if (!raw) return fallback;
  const n = parseFloat(String(raw).replace(/%/g, '').trim());
  if (Number.isNaN(n)) return fallback;
  return Math.min(100, Math.max(0, n));
}

function hasPinContent(pin: PinData): boolean {
  return !!(pin.name && String(pin.name).trim());
}


function viewImageFallback(path: string): string {
  switch (path) {
    case 'neighborhoodmap.view1_image': return imgFrontView;
    case 'neighborhoodmap.view2_image': return imgSouthView;
    case 'neighborhoodmap.view3_image': return imgEastView;
    default: return '';
  }
}

function pinImageFallback(path: string): string {
  switch (path) {
    case 'neighborhoodmap.view1_pin1': return imgMoore;
    case 'neighborhoodmap.view1_pin2': return imgOlympic;
    case 'neighborhoodmap.view2_pin1': return imgMopop;
    case 'neighborhoodmap.view2_pin2': return imgSeattleCenter;
    case 'neighborhoodmap.view3_pin1': return imgRainier;
    case 'neighborhoodmap.view3_pin2': return imgBainbridge;
    case 'neighborhoodmap.view3_pin3': return imgPike;
    case 'neighborhoodmap.view3_pin4': return imgTMobile;
    case 'neighborhoodmap.view3_pin5': return imgLumen;
    case 'neighborhoodmap.view3_pin6': return imgWheel;
    case 'neighborhoodmap.view3_pin7': return imgBainbridge;
    default: return '';
  }
}

// ============================================================================
// Hotspot pin
// ============================================================================

function HotspotPin({
  path,
  definition,
  pin,
  isOpen,
  onToggle,
  isEditing,
}: {
  path: string;
  definition: SlotGroupDefinition;
  pin: PinData;
  isOpen: boolean;
  onToggle: () => void;
  isEditing: boolean;
}) {
  const visible = hasPinContent(pin) || isEditing;
  if (!visible) return null;

  const top = parsePercent(pin.top, 50);
  const left = parsePercent(pin.left, 50);
  const empty = !hasPinContent(pin);
  const tipImage = pin.image || pinImageFallback(path) || '';

  return (
    <div
      className="nb-hotspot__pin-wrap"
      style={{ top: `${top}%`, left: `${left}%` }}
    >
      <EditableSlotGroup groupPath={path} definition={definition} className="nb-hotspot__pin-edit">
        <button
          type="button"
          className={`nb-hotspot__pin${isOpen ? ' is-open' : ''}${empty ? ' is-empty' : ''}`}
          aria-expanded={isOpen}
          aria-label={pin.name || 'Add nearby place'}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          <span className="nb-hotspot__pulse" aria-hidden="true" />
          <span className="nb-hotspot__dot" aria-hidden="true" />
        </button>
      </EditableSlotGroup>

      {isOpen && hasPinContent(pin) ? (
        <div className="nb-hotspot__tip" role="dialog" aria-label={pin.name || 'Place details'}>
          <button
            type="button"
            className="nb-hotspot__tip-close"
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            ×
          </button>
          {tipImage ? (
            <div className="nb-hotspot__tip-media">
              <VibesitesImage
                url={tipImage}
                alt=""
                sizes="160px"
                className="nb-hotspot__tip-img"
              />
            </div>
          ) : null}
          <div className={`nb-hotspot__tip-body${!tipImage ? ' nb-hotspot__tip-body--full' : ''}`}>
            <p className="nb-hotspot__tip-name">
              {pin.name}
              {pin.address ? <span className="nb-hotspot__tip-sep"> – </span> : null}
              {pin.address ? <span className="nb-hotspot__tip-address">{pin.address}</span> : null}
            </p>
            {pin.url ? (
              <a
                href={pin.url}
                target={pin._target || '_blank'}
                rel="noopener noreferrer"
                className="nb-hotspot__tip-link"
                onClick={(e) => e.stopPropagation()}
              >
                Learn More
              </a>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ============================================================================
// View panel
// ============================================================================

function ViewPanel({
  imagePath,
  imageDef,
  image,
  pinDefs,
  pins,
  isEditing,
}: {
  imagePath: string;
  imageDef: SlotGroupDefinition;
  image: { image?: string | null; alt?: string | null };
  pinDefs: { path: string; definition: SlotGroupDefinition }[];
  pins: PinData[];
  isEditing: boolean;
}) {
  const [openPin, setOpenPin] = useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (openPin === null) return;
    const onDoc = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpenPin(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [openPin]);

  const src = image.image || viewImageFallback(imagePath) || '';

  return (
    <div ref={panelRef} className="nb-hotspot__stage">
      <EditableSlotGroup groupPath={imagePath} definition={imageDef} className="nb-hotspot__image-edit">
        {src ? (
          <VibesitesImage
            url={src}
            alt={image.alt || ''}
            sizes="(min-width: 1200px) 1100px, 100vw"
            className="nb-hotspot__image"
          />
        ) : (
          <div className="nb-hotspot__image-placeholder">
            <span className="font-body text-sm text-[var(--vibe-color-text-muted)]">
              {isEditing ? 'Add view image' : 'Neighborhood view coming soon.'}
            </span>
          </div>
        )}
      </EditableSlotGroup>

      {pinDefs.map((item, i) => (
        <HotspotPin
          key={item.path}
          path={item.path}
          definition={item.definition}
          pin={pins[i] || {}}
          isOpen={openPin === i}
          onToggle={() => setOpenPin((cur) => (cur === i ? null : i))}
          isEditing={isEditing}
        />
      ))}
    </div>
  );
}

// ============================================================================
// Main
// ============================================================================

export function NeighborhoodMap() {
  const editMode = useEditMode();
  const isEditing = !!editMode?.isEditMode;

  const view1Label = useContentToken('neighborhoodmap.view1_label');
  const view2Label = useContentToken('neighborhoodmap.view2_label');
  const view3Label = useContentToken('neighborhoodmap.view3_label');

  const view1Image = useSlotGroup('neighborhoodmap.view1_image', view1ImageDef);
  const view2Image = useSlotGroup('neighborhoodmap.view2_image', view2ImageDef);
  const view3Image = useSlotGroup('neighborhoodmap.view3_image', view3ImageDef);

  const v1p1 = useSlotGroup('neighborhoodmap.view1_pin1', view1Pin1Def);
  const v1p2 = useSlotGroup('neighborhoodmap.view1_pin2', view1Pin2Def);
  const v1p3 = useSlotGroup('neighborhoodmap.view1_pin3', view1Pin3Def);
  const v2p1 = useSlotGroup('neighborhoodmap.view2_pin1', view2Pin1Def);
  const v2p2 = useSlotGroup('neighborhoodmap.view2_pin2', view2Pin2Def);
  const v2p3 = useSlotGroup('neighborhoodmap.view2_pin3', view2Pin3Def);
  const v3p1 = useSlotGroup('neighborhoodmap.view3_pin1', view3Pin1Def);
  const v3p2 = useSlotGroup('neighborhoodmap.view3_pin2', view3Pin2Def);
  const v3p3 = useSlotGroup('neighborhoodmap.view3_pin3', view3Pin3Def);
  const v3p4 = useSlotGroup('neighborhoodmap.view3_pin4', view3Pin4Def);
  const v3p5 = useSlotGroup('neighborhoodmap.view3_pin5', view3Pin5Def);
  const v3p6 = useSlotGroup('neighborhoodmap.view3_pin6', view3Pin6Def);
  const v3p7 = useSlotGroup('neighborhoodmap.view3_pin7', view3Pin7Def);

  const [activeView, setActiveView] = useState(0);

  return (
    <section className="nb-page">
      <div className="nb-page__intro">
        <RichTextContent
          path="neighborhoodmap.subtitle"
          defaultValue="<p>High-Rise Sophistication in the Heart of Downtown Seattle</p>"
          as="h2"
          className="nb-page__subtitle font-heading"
        />
      </div>

      <div className="nb-hotspot">
        <div className="nb-hotspot__tabs" role="tablist" aria-label="Neighborhood views">
          <button
            type="button"
            role="tab"
            aria-selected={activeView === 0}
            className={`nb-hotspot__tab${activeView === 0 ? ' is-active' : ''}`}
            onClick={() => setActiveView(0)}
          >
            <EditableToken path="neighborhoodmap.view1_label" as="span" className="nb-hotspot__tab-label">
              {view1Label || 'FRONT VIEW'}
            </EditableToken>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeView === 1}
            className={`nb-hotspot__tab${activeView === 1 ? ' is-active' : ''}`}
            onClick={() => setActiveView(1)}
          >
            <EditableToken path="neighborhoodmap.view2_label" as="span" className="nb-hotspot__tab-label">
              {view2Label || 'SOUTH VIEW'}
            </EditableToken>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeView === 2}
            className={`nb-hotspot__tab${activeView === 2 ? ' is-active' : ''}`}
            onClick={() => setActiveView(2)}
          >
            <EditableToken path="neighborhoodmap.view3_label" as="span" className="nb-hotspot__tab-label">
              {view3Label || 'EAST VIEW'}
            </EditableToken>
          </button>
        </div>

        <div className={activeView === 0 ? undefined : 'hidden'} hidden={activeView !== 0} aria-hidden={activeView !== 0}>
          <ViewPanel
            imagePath="neighborhoodmap.view1_image"
            imageDef={view1ImageDef}
            image={view1Image}
            pinDefs={[
              { path: 'neighborhoodmap.view1_pin1', definition: view1Pin1Def },
              { path: 'neighborhoodmap.view1_pin2', definition: view1Pin2Def },
              { path: 'neighborhoodmap.view1_pin3', definition: view1Pin3Def },
            ]}
            pins={[v1p1, v1p2, v1p3]}
            isEditing={isEditing}
          />
        </div>
        <div className={activeView === 1 ? undefined : 'hidden'} hidden={activeView !== 1} aria-hidden={activeView !== 1}>
          <ViewPanel
            imagePath="neighborhoodmap.view2_image"
            imageDef={view2ImageDef}
            image={view2Image}
            pinDefs={[
              { path: 'neighborhoodmap.view2_pin1', definition: view2Pin1Def },
              { path: 'neighborhoodmap.view2_pin2', definition: view2Pin2Def },
              { path: 'neighborhoodmap.view2_pin3', definition: view2Pin3Def },
            ]}
            pins={[v2p1, v2p2, v2p3]}
            isEditing={isEditing}
          />
        </div>
        <div className={activeView === 2 ? undefined : 'hidden'} hidden={activeView !== 2} aria-hidden={activeView !== 2}>
          <ViewPanel
            imagePath="neighborhoodmap.view3_image"
            imageDef={view3ImageDef}
            image={view3Image}
            pinDefs={[
              { path: 'neighborhoodmap.view3_pin1', definition: view3Pin1Def },
              { path: 'neighborhoodmap.view3_pin2', definition: view3Pin2Def },
              { path: 'neighborhoodmap.view3_pin3', definition: view3Pin3Def },
              { path: 'neighborhoodmap.view3_pin4', definition: view3Pin4Def },
              { path: 'neighborhoodmap.view3_pin5', definition: view3Pin5Def },
              { path: 'neighborhoodmap.view3_pin6', definition: view3Pin6Def },
              { path: 'neighborhoodmap.view3_pin7', definition: view3Pin7Def },
            ]}
            pins={[v3p1, v3p2, v3p3, v3p4, v3p5, v3p6, v3p7]}
            isEditing={isEditing}
          />
        </div>
      </div>

      {/* Schema-lint anchors — static path literals the extractor can see */}
      <div className="hidden" style={{ display: 'none' }} aria-hidden="true">
        <EditableSlotGroup groupPath="neighborhoodmap.view1_image" definition={view1ImageDef}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view2_image" definition={view2ImageDef}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view3_image" definition={view3ImageDef}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view1_pin1" definition={view1Pin1Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view1_pin2" definition={view1Pin2Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view1_pin3" definition={view1Pin3Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view2_pin1" definition={view2Pin1Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view2_pin2" definition={view2Pin2Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view2_pin3" definition={view2Pin3Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view3_pin1" definition={view3Pin1Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view3_pin2" definition={view3Pin2Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view3_pin3" definition={view3Pin3Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view3_pin4" definition={view3Pin4Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view3_pin5" definition={view3Pin5Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view3_pin6" definition={view3Pin6Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodmap.view3_pin7" definition={view3Pin7Def}><span /></EditableSlotGroup>
      </div>

      <style>{`
        .nb-page {
          background: #fff;
          padding: 3.125rem 1.25rem 4rem;
        }
        .nb-page__intro {
          max-width: 52.5rem;
          margin: 0 auto 3.25rem;
          text-align: center;
        }
        .nb-page__subtitle,
        .nb-page__subtitle * {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.35rem, 2.4vw, 1.875rem);
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
          margin: 0;
        }
        .nb-page__subtitle p {
          margin: 0;
        }

        .nb-hotspot {
          max-width: 72rem;
          margin: 0 auto;
        }
        .nb-hotspot__tabs {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }
        .nb-hotspot__tab {
          appearance: none;
          background: #e6e6e6;
          border: none;
          border-radius: 0;
          padding: 0.7rem 1.6rem;
          cursor: pointer;
          color: #333;
          transition: background 0.2s ease, color 0.2s ease;
        }
        .nb-hotspot__tab:hover {
          background: #d8d8d8;
          color: #111;
        }
        .nb-hotspot__tab.is-active {
          background: #333;
          color: #fff;
        }
        .nb-hotspot__tab.is-active:hover {
          background: #222;
          color: #fff;
        }
        .nb-hotspot__tab-label,
        .nb-hotspot__tab-label * {
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: inherit;
        }

        .nb-hotspot__stage {
          position: relative;
          width: 100%;
          overflow: visible;
          background: #f3f3f3;
        }
        .nb-hotspot__image-edit {
          display: block;
          width: 100%;
        }
        .nb-hotspot__image {
          display: block;
          width: 100%;
          height: auto;
          max-height: none;
          object-fit: cover;
        }
        .nb-hotspot__image-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 20rem;
          background: #ececec;
        }
        .nb-hotspot__empty {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 16rem;
          background: #f7f7f7;
        }

        .nb-hotspot__pin-wrap {
          position: absolute;
          z-index: 5;
          transform: translate(-50%, -50%);
        }
        .nb-hotspot__pin-edit {
          display: block;
        }
        .nb-hotspot__pin {
          position: relative;
          width: 1.85rem;
          height: 1.85rem;
          padding: 0;
          border: none;
          background: transparent;
          cursor: pointer;
        }
        .nb-hotspot__dot {
          position: absolute;
          inset: 22%;
          border-radius: 50%;
          background: #fff;
          border: 2px solid var(--vibe-color-secondary);
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
          z-index: 2;
          box-sizing: border-box;
        }
        .nb-hotspot__pulse {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: color-mix(in srgb, var(--vibe-color-secondary) 28%, transparent);
          animation: nb-pin-pulse 2s ease-out infinite;
          pointer-events: none;
        }
        .nb-hotspot__pin.is-open .nb-hotspot__dot {
          background: var(--vibe-color-secondary);
          border-color: #fff;
        }
        .nb-hotspot__pin.is-empty .nb-hotspot__dot {
          background: #fff;
          border-color: #b6b6b6;
        }
        .nb-hotspot__pin.is-empty .nb-hotspot__pulse {
          animation: none;
          background: rgba(0, 0, 0, 0.1);
        }

        .nb-hotspot__tip {
          position: absolute;
          left: 50%;
          bottom: calc(100% + 0.75rem);
          transform: translateX(-50%);
          width: min(20rem, 70vw);
          display: flex;
          gap: 0.65rem;
          padding: 0.65rem;
          background: rgba(20, 20, 20, 0.92);
          color: #fff;
          box-shadow: 0 10px 28px rgba(0, 0, 0, 0.28);
          z-index: 20;
          animation: nb-tip-in 0.2s ease;
        }
        .nb-hotspot__tip-close {
          position: absolute;
          top: 0.2rem;
          right: 0.35rem;
          border: none;
          background: transparent;
          color: #fff;
          font-size: 1.1rem;
          line-height: 1;
          cursor: pointer;
          opacity: 0.8;
        }
        .nb-hotspot__tip-media {
          width: 6.5rem;
          height: 4.35rem;
          overflow: hidden;
          background: #333;
        }
        .nb-hotspot__tip-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .nb-hotspot__tip-body {
          min-width: 0;
          flex: 1;
          padding-right: 0.85rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 0.35rem;
        }
        .nb-hotspot__tip-body--full {
          padding-right: 1rem;
        }
        .nb-hotspot__tip-name {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 0.82rem;
          font-weight: 700;
          line-height: 1.35;
          color: #fff;
        }
        .nb-hotspot__tip-address,
        .nb-hotspot__tip-sep {
          font-weight: 400;
          color: rgba(255, 255, 255, 0.88);
        }
        .nb-hotspot__tip-link {
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--vibe-color-secondary);
          text-decoration: none;
        }
        .nb-hotspot__tip-link:hover {
          color: #fff;
        }

        @keyframes nb-pin-pulse {
          0% { transform: scale(0.55); opacity: 0.85; }
          70% { transform: scale(1.35); opacity: 0; }
          100% { transform: scale(1.35); opacity: 0; }
        }
        @keyframes nb-tip-in {
          from { opacity: 0; transform: translateX(-50%) translateY(4px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .nb-hotspot__pulse,
          .nb-hotspot__tip {
            animation: none;
          }
        }

        @media (max-width: 767px) {
          .nb-page {
            padding: 2.25rem 1rem 2.75rem;
          }
          .nb-page__intro {
            margin-bottom: 2.25rem;
          }
          .nb-hotspot__tabs {
            gap: 0.4rem;
          }
          .nb-hotspot__tab {
            padding: 0.6rem 1.1rem;
          }
          .nb-hotspot__tab-label,
          .nb-hotspot__tab-label * {
            font-size: 0.72rem;
          }
          .nb-hotspot__tip {
            flex-direction: column;
            width: min(16rem, 78vw);
          }
          .nb-hotspot__tip-media {
            width: 100%;
            height: 7rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodMap',
  displayName: 'Neighborhood Map',
  tokens: {
    'neighborhoodmap.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>High-Rise Sophistication in the Heart of Downtown Seattle</p>',
    },
    'neighborhoodmap.view1_label': {
      label: 'View 1 Tab Label',
      type: 'text',
      defaultValue: 'FRONT VIEW',
    },
    'neighborhoodmap.view2_label': {
      label: 'View 2 Tab Label',
      type: 'text',
      defaultValue: 'SOUTH VIEW',
    },
    'neighborhoodmap.view3_label': {
      label: 'View 3 Tab Label',
      type: 'text',
      defaultValue: 'EAST VIEW',
    },
  },
  slotGroups: {
    'neighborhoodmap.view1_image': view1ImageDef,
    'neighborhoodmap.view2_image': view2ImageDef,
    'neighborhoodmap.view3_image': view3ImageDef,
    'neighborhoodmap.view1_pin1': view1Pin1Def,
    'neighborhoodmap.view1_pin2': view1Pin2Def,
    'neighborhoodmap.view1_pin3': view1Pin3Def,
    'neighborhoodmap.view2_pin1': view2Pin1Def,
    'neighborhoodmap.view2_pin2': view2Pin2Def,
    'neighborhoodmap.view2_pin3': view2Pin3Def,
    'neighborhoodmap.view3_pin1': view3Pin1Def,
    'neighborhoodmap.view3_pin2': view3Pin2Def,
    'neighborhoodmap.view3_pin3': view3Pin3Def,
    'neighborhoodmap.view3_pin4': view3Pin4Def,
    'neighborhoodmap.view3_pin5': view3Pin5Def,
    'neighborhoodmap.view3_pin6': view3Pin6Def,
    'neighborhoodmap.view3_pin7': view3Pin7Def,
  },
};

(NeighborhoodMap as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodMap;
